'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import { useNotification } from '@/contexts/NotificationContext';
import { useInventoryAuditorLocationsList } from '@/hooks/useInventoryAuditorLocation';
import {
  exportIaStoreCountsReport,
  fetchIaStoreCountDates,
  queryIaStoreCountsReport,
  type IaStoreCountReportRow,
  type IaStoreCountReportTotals,
} from '@/hooks/useInventoryAuditorReports';
import { inventoryAuditorCatalogOriginLabel } from '@/stack-shared/constants/inventory-auditor-catalog.constants';

const PAGE_SIZE = 50;

type LocRow = { id: number; name?: string };

function displayUpc(raw: string | null | undefined): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '—';
  if (digits.length === 13 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

function formatCountDateLabel(ymd: string): string {
  const parts = ymd.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return ymd;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const InventoryAuditorStoreCountsReportPage: React.FC = () => {
  const { showError, showSuccess } = useNotification();
  const { data: locations = [], isLoading: locLoading } = useInventoryAuditorLocationsList();

  const [countDates, setCountDates] = useState<string[]>([]);
  const [datesLoading, setDatesLoading] = useState(true);
  const [countDate, setCountDate] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<number | ''>('');
  const [rows, setRows] = useState<IaStoreCountReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState<IaStoreCountReportTotals | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setDatesLoading(true);
    void (async () => {
      try {
        const { dates } = await fetchIaStoreCountDates({
          locationId: locationId === '' ? undefined : Number(locationId),
        });
        if (cancelled) return;
        setCountDates(dates);
        setCountDate((prev) => {
          if (prev && dates.includes(prev)) return prev;
          return dates[0] ?? null;
        });
        setPageIndex(0);
      } catch (e: unknown) {
        if (!cancelled) {
          showError(e instanceof Error ? e.message : 'Failed to load count dates');
          setCountDates([]);
          setCountDate(null);
        }
      } finally {
        if (!cancelled) setDatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locationId, showError]);

  const fetchData = useCallback(async () => {
    if (!countDate) {
      setRows([]);
      setTotal(0);
      setTotals(null);
      return;
    }
    setIsLoading(true);
    try {
      const r = await queryIaStoreCountsReport({
        countDate,
        locationId: locationId === '' ? undefined : Number(locationId),
        skip: pageIndex * PAGE_SIZE,
        take: PAGE_SIZE,
      });
      setRows(r.rows);
      setTotal(r.total);
      setTotals(r.totals);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load store counts report');
      setRows([]);
      setTotal(0);
      setTotals(null);
    } finally {
      setIsLoading(false);
    }
  }, [countDate, locationId, pageIndex, showError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleExport = useCallback(async () => {
    if (!countDate) {
      showError('Select a count date with readings first.');
      return;
    }
    setExporting(true);
    try {
      await exportIaStoreCountsReport({
        countDate,
        locationId: locationId === '' ? undefined : Number(locationId),
      });
      showSuccess('CSV downloaded');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [countDate, locationId, showError, showSuccess]);

  const locOpts = locations as LocRow[];
  const selectedLoc = locationId === '' ? null : locOpts.find((l) => l.id === locationId) ?? null;

  const columns = useMemo<MRT_ColumnDef<IaStoreCountReportRow>[]>(
    () => [
      { accessorKey: 'countDate', header: 'Count date', size: 120 },
      {
        accessorKey: 'locationName',
        header: 'Location',
        size: 180,
        Cell: ({ row }) => row.original.locationName?.trim() || `Location ${row.original.locationId}`,
      },
      {
        accessorKey: 'upc',
        header: 'UPC',
        size: 130,
        Cell: ({ cell }) => displayUpc(cell.getValue<string>()),
      },
      {
        accessorKey: 'itemName',
        header: 'Item',
        size: 220,
        Cell: ({ cell }) => cell.getValue<string | null>()?.trim() || '—',
      },
      { accessorKey: 'uomPackType', header: 'UOM', size: 90 },
      { accessorKey: 'size', header: 'Size', size: 90 },
      { accessorKey: 'quantity', header: 'Qty', size: 70 },
      {
        accessorKey: 'catalogOrigin',
        header: 'Origin',
        size: 100,
        Cell: ({ cell }) => inventoryAuditorCatalogOriginLabel(cell.getValue<string>()),
      },
      {
        accessorKey: 'lastUpdated',
        header: 'Updated',
        size: 140,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? new Date(v).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—';
        },
      },
    ],
    [],
  );

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Reports', href: '/app/inventory-auditor/reports' },
    { label: 'Store counts' },
  ];

  return (
    <Box>
      <TitleBar breadcrumbs={breadcrumbs} title="Store counts" />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          Defaults to the most recent count day. Pick any day that has readings, optionally filter
          by location, then download CSV.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 280 }}
            options={countDates}
            loading={datesLoading}
            value={countDate}
            onChange={(_, v) => {
              setCountDate(v);
              setPageIndex(0);
            }}
            getOptionLabel={(ymd) => `${formatCountDateLabel(ymd)} (${ymd})`}
            filterOptions={(options, state) => {
              const q = state.inputValue.trim().toLowerCase();
              if (!q) return options;
              return options.filter((ymd) => {
                const label = formatCountDateLabel(ymd).toLowerCase();
                return ymd.includes(q) || label.includes(q);
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Count date"
                placeholder={datesLoading ? 'Loading…' : 'Search count days…'}
                helperText={
                  !datesLoading && countDates.length === 0
                    ? 'No count readings found'
                    : `${countDates.length.toLocaleString()} day(s) with readings`
                }
              />
            )}
            noOptionsText="No matching count days"
          />
          <Autocomplete<LocRow>
            size="small"
            sx={{ minWidth: 260 }}
            options={locOpts}
            loading={locLoading}
            value={selectedLoc}
            onChange={(_, v) => {
              setLocationId(v?.id ?? '');
              setPageIndex(0);
            }}
            getOptionLabel={(o) => o.name?.trim() || `Location ${o.id}`}
            renderInput={(params) => <TextField {...params} label="Location (optional)" />}
          />
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => void handleExport()}
            disabled={exporting || isLoading || !countDate}
            sx={{ textTransform: 'none' }}
          >
            {exporting ? 'Downloading…' : 'Download CSV'}
          </Button>
          <Button
            component={RouterLink}
            to="/app/inventory-auditor/reports"
            variant="text"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            All reports
          </Button>
        </Box>

        {totals && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip size="small" label={`${totals.locationCount.toLocaleString()} locations`} />
            <Chip size="small" label={`${totals.lineCount.toLocaleString()} lines`} />
            <Chip size="small" label={`${totals.totalUnits.toLocaleString()} units`} />
          </Box>
        )}

        <MaterialReactTable
          columns={columns}
          data={rows}
          state={{ isLoading: isLoading || datesLoading, pagination: { pageIndex, pageSize: PAGE_SIZE } }}
          manualPagination
          rowCount={total}
          onPaginationChange={(updater) => {
            const next =
              typeof updater === 'function' ? updater({ pageIndex, pageSize: PAGE_SIZE }) : updater;
            setPageIndex(next.pageIndex);
          }}
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          muiPaginationProps={{ rowsPerPageOptions: [PAGE_SIZE] }}
        />
      </Box>
    </Box>
  );
};

export default InventoryAuditorStoreCountsReportPage;
